import React, { useState, useEffect } from 'react';
import { useAppStore, useWallet } from '../stores/useAppStore';
import { useSetCurrentPage } from '../stores/useAppStore';
import GradientBackground from '../components/ui/GradientBackground';
import ParticleBackground from '../components/ui/ParticleBackground';
import PageHeader from '../components/PageHeader';
import { useToastStore } from '../stores/useToastStore';
import GlassCard from '../components/ui/GlassCard';

const Withdraw: React.FC = () => {
  const setCurrentPage = useSetCurrentPage();
  const wallet = useWallet();
  const fetchWallet = useAppStore((state) => state.fetchWallet);
  const showToast = useToastStore((state) => state.show);

  const [address, setAddress] = useState('');
  const [amount, setAmount] = useState('');
  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    // 只有在没有钱包或者需要刷新当前钱包余额时才调用
    // 如果已经有选中的钱包，使用该钱包的网络和币种进行刷新
    if (wallet) {
      fetchWallet(wallet.currency, wallet.chain);
    } else {
      fetchWallet();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fetchWallet]);

  const handleWithdraw = async () => {
    if (!address || !amount) {
      showToast('请填写地址和金额', 'error');
      return;
    }

    if (parseFloat(amount) > (wallet?.balance || 0)) {
      showToast('余额不足', 'error');
      return;
    }

    try {
      setIsLoading(true);
      // 这里原本应该调用 withdraw API，现在先显示功能开发中
      // await walletService.withdraw({ address, amount, network });
      showToast('提现请求已提交，正在审核中', 'success');
      setCurrentPage('wallet');
    } catch (error: any) {
      showToast(error.message || '提现失败', 'error');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <GradientBackground>
      <div className="min-h-screen">
        <ParticleBackground />
        <PageHeader title="提币" onBack={() => setCurrentPage('wallet')} />

        <div className="px-4 py-4 pt-24 max-w-lg mx-auto">
          <GlassCard className="p-6 mb-6 overflow-hidden relative" hover={false}>
            {/* 装饰性背景 */}
            <div className="absolute top-0 right-0 -mr-16 -mt-16 w-32 h-32 bg-indigo-500/10 rounded-full blur-3xl animate-pulse"></div>
            <div className="absolute bottom-0 left-0 -ml-16 -mb-16 w-32 h-32 bg-purple-500/10 rounded-full blur-3xl transition-all duration-700"></div>

            <div className="relative z-10">
              <div className="flex justify-between items-center mb-8 px-1">
                <div className="text-white/80 text-[10px] font-black uppercase tracking-[0.2em]">
                  提币网络 (Network)
                </div>
                <div className="bg-indigo-500/10 text-indigo-400 px-4 py-1.5 rounded-xl border border-indigo-500/20 font-black text-[10px] uppercase tracking-widest shadow-lg shadow-indigo-500/10">
                  {wallet?.chain || 'POLYGON'}
                </div>
              </div>

              <div className="space-y-6">
                <div>
                  <div className="flex justify-between items-center mb-2 px-1">
                    <label className="text-white/80 text-[10px] font-black uppercase tracking-[0.2em]">
                      USDT 提币地址 ({wallet?.chain || 'POLYGON'})
                    </label>
                  </div>
                  <div className="relative group">
                    <div className="absolute -inset-0.5 bg-gradient-to-r from-indigo-500/20 to-purple-500/20 rounded-2xl blur opacity-0 group-focus-within:opacity-100 transition duration-500"></div>
                    <input
                      type="text"
                      placeholder={`请输入 ${wallet?.chain || 'POLYGON'} 网络地址`}
                      value={address}
                      onChange={(e) => setAddress(e.target.value)}
                      className="relative w-full bg-black/40 border border-white/10 rounded-2xl px-5 py-4 text-white placeholder:text-white/40 focus:outline-none focus:border-indigo-500/50 transition-all font-medium text-sm"
                    />
                  </div>
                </div>

                <div>
                  <div className="flex justify-between items-center mb-2 px-1">
                    <label className="text-white/80 text-[10px] font-black uppercase tracking-[0.2em]">
                      提币金额 (USDT)
                    </label>
                    <div className="text-white/80 text-[10px] font-black uppercase tracking-widest">
                      可用:{' '}
                      <span className="text-white">
                        {typeof wallet?.balance === 'number' ? wallet.balance.toFixed(2) : '0.00'}
                      </span>
                    </div>
                  </div>
                  <div className="relative group">
                    <div className="absolute -inset-0.5 bg-gradient-to-r from-indigo-500/20 to-purple-500/20 rounded-2xl blur opacity-0 group-focus-within:opacity-100 transition duration-500"></div>
                    <input
                      type="number"
                      placeholder="最小 10 USDT"
                      value={amount}
                      onChange={(e) => setAmount(e.target.value)}
                      className="relative w-full bg-black/40 border border-white/10 rounded-2xl px-5 py-4 text-white placeholder:text-white/40 focus:outline-none focus:border-indigo-500/50 transition-all font-black text-xl tracking-tight"
                    />
                    <button
                      onClick={() => setAmount(wallet?.balance.toString() || '0')}
                      className="absolute right-4 top-1/2 -translate-y-1/2 bg-indigo-500/10 hover:bg-indigo-500/20 text-indigo-400 px-3 py-1.5 rounded-lg border border-indigo-500/20 text-[10px] font-black uppercase tracking-widest transition-all"
                    >
                      MAX
                    </button>
                  </div>
                  <div className="flex justify-between mt-3 px-2">
                    <div className="text-white/70 text-[10px] font-bold uppercase tracking-widest flex items-center gap-1.5">
                      <span className="w-1 h-1 bg-white/60 rounded-full"></span>
                      手续费: 1.00 USDT
                    </div>
                  </div>
                </div>

                <button
                  onClick={handleWithdraw}
                  disabled={isLoading || !address || !amount}
                  className={`w-full py-5 rounded-2xl font-black uppercase tracking-[0.3em] text-xs transition-all relative overflow-hidden group/btn ${
                    isLoading || !address || !amount
                      ? 'bg-white/5 text-white/40 cursor-not-allowed border border-white/5'
                      : 'bg-indigo-500 text-white shadow-xl shadow-indigo-500/20 hover:scale-[1.02] active:scale-[0.98]'
                  }`}
                >
                  {isLoading ? (
                    <div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin mx-auto"></div>
                  ) : (
                    <>
                      <span className="relative z-10">立即提币</span>
                      {!isLoading && address && amount && (
                        <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/10 to-transparent -translate-x-full group-hover/btn:animate-shimmer"></div>
                      )}
                    </>
                  )}
                </button>
              </div>

              <div className="mt-8 relative group/note">
                <div className="absolute inset-0 bg-red-500/5 rounded-2xl blur-xl opacity-50 group-hover/note:opacity-100 transition-opacity"></div>
                <div className="relative bg-red-500/5 border border-red-500/10 rounded-2xl p-5 text-[10px] text-red-400 leading-relaxed font-bold tracking-wide">
                  <div className="text-red-500 font-black mb-3 flex items-center gap-2 uppercase tracking-[0.2em]">
                    <span className="w-1.5 h-1.5 bg-red-500 rounded-full animate-pulse"></span>
                    提币须知 (Notes)
                  </div>
                  <ul className="space-y-2 list-none">
                    <li className="flex gap-2">
                      <span className="text-red-500/70">01.</span>
                      提币一旦发出无法撤回，请务必仔细核对目标地址
                    </li>
                    <li className="flex gap-2">
                      <span className="text-red-500/70">02.</span>
                      提币网络必须与您的钱包网络完全一致，否则将导致资产永久丢失
                    </li>
                    <li className="flex gap-2">
                      <span className="text-red-500/70">03.</span>
                      审核通过后，资金将在 10-30 分钟内发出，请耐心等待
                    </li>
                  </ul>
                </div>
              </div>
            </div>
          </GlassCard>
        </div>
      </div>
    </GradientBackground>
  );
};

export default Withdraw;
